import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { Phone, MessageCircle, Users, CalendarCheck, Target, PieChart } from 'lucide-react'
import type { BdrStats } from '@/types'

// ─── Circular Progress ───────────────────────────────────────────────────────

interface CircularProgressProps {
  size?: number
  strokeWidth?: number
  percentage?: number
  color?: string
  children?: React.ReactNode
}

function CircularProgress({
  size = 80,
  strokeWidth = 6,
  percentage = 0,
  color = '#3B82F6',
  children,
}: CircularProgressProps) {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (Math.min(percentage, 100) / 100) * circumference

  return (
    <div className='relative inline-flex items-center justify-center' style={{ width: size, height: size }}>
      <svg width={size} height={size} className='-rotate-90'>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill='none'
          stroke='rgba(255,255,255,0.06)'
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill='none'
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap='round'
          className='transition-all duration-700 ease-out'
        />
      </svg>
      <div className='absolute inset-0 flex items-center justify-center'>{children}</div>
    </div>
  )
}

// ─── KPI Card ────────────────────────────────────────────────────────────────

interface KpiConfig {
  key: keyof BdrStats['hoje']
  label: string
  meta: number
  color: string
  icon: React.ElementType
}

const KPI_CONFIG: KpiConfig[] = [
  { key: 'ligacoes', label: 'LIGAÇÕES', meta: 60, color: '#3B82F6', icon: Phone },
  { key: 'whatsapp', label: 'WHATSAPP', meta: 50, color: '#10B981', icon: MessageCircle },
  { key: 'contatos', label: 'CONTATOS', meta: 15, color: '#14BDBD', icon: Users },
  { key: 'reunioes', label: 'REUNIÕES', meta: 2, color: '#8B5CF6', icon: CalendarCheck },
]

// ─── Component ───────────────────────────────────────────────────────────────

interface BdrPulseDashboardProps {
  stats: BdrStats
  planningCount: number
}

export function BdrPulseDashboard({ stats, planningCount }: BdrPulseDashboardProps) {
  const totalEsforco = useMemo(() => {
    const hoje = stats?.hoje || { ligacoes: 0, whatsapp: 0, contatos: 0, reunioes: 0 }
    const { ligacoes = 0, whatsapp = 0, contatos = 0, reunioes = 0 } = hoje
    const pctLig = Math.min((ligacoes / 60) * 100, 100)
    const pctWha = Math.min((whatsapp / 50) * 100, 100)
    const pctCon = Math.min((contatos / 15) * 100, 100)
    const pctReu = Math.min((reunioes / 2) * 100, 100)
    return Math.round((pctLig + pctWha + pctCon + pctReu) / 4)
  }, [stats?.hoje])

  const resultsStats = useMemo(() => {
    return [
      { name: 'Agendados', value: stats?.mes?.reunioesAgendadas || 0, target: 35, color: '#F59E0B' },
      { name: 'Realizados', value: stats?.mes?.reunioesRealizadas || 0, target: 20, color: '#EC4899' },
      { name: 'Conversão SQL', value: `${stats?.conversionRate || 0}%`, target: null, color: '#10B981' },
      { name: 'No-show', value: `${stats?.noShowRate || 0}%`, target: null, color: '#EF4444' },
      { name: 'Prontos p/ Amanhã', value: planningCount, target: 30, color: '#8B5CF6' }
    ]
  }, [stats?.mes, stats?.conversionRate, stats?.noShowRate, planningCount])

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className='flex flex-col lg:flex-row gap-4'
    >
      {/* ── Esquerda: Esforço Diário ────────────────────────────────────── */}
      <div className='flex-[1.8] glass-card border-white/[0.06] p-5 flex flex-col'>
        <div className='flex items-center justify-between mb-5'>
          <div className='flex items-center gap-2 text-text-primary font-extrabold text-xs uppercase tracking-widest'>
            <Target size={15} className='text-accent shrink-0' />
            Esforço Diário
          </div>
          <span className='text-[10px] font-bold text-text-muted bg-white/[0.04] px-2.5 py-1 rounded-lg border border-white/[0.06] uppercase tracking-wider'>
            PERFORMANCE HOJE: <span className='text-accent'>{totalEsforco}%</span>
          </span>
        </div>

        <div className='grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1'>
          {KPI_CONFIG.map(({ key, label, meta, color, icon: Icon }) => {
            const value = stats?.hoje?.[key] ?? 0
            const pct = Math.min(Math.round((value / meta) * 100), 100)

            return (
              <div
                key={key}
                className='flex flex-col items-center justify-center gap-3 p-4 bg-white/[0.02] border border-white/[0.04] rounded-2xl transition-all hover:bg-white/[0.04] hover:border-accent/20 hover:-translate-y-0.5'
              >
                <CircularProgress size={70} strokeWidth={4} percentage={pct} color={color}>
                  <Icon size={20} style={{ color, opacity: 0.9 }} />
                </CircularProgress>
                
                <div className='text-center'>
                  <div className='text-text-primary text-lg font-extrabold leading-tight'>
                    {value}
                    <span className='text-text-muted text-xs font-bold ml-1'>/ {meta}</span>
                  </div>
                  <div className='text-[9px] font-black text-text-muted uppercase tracking-widest mt-1'>
                    {label}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Direita: Resultados & Prep ─────────────────────────────────── */}
      <div className='flex-1 glass-card border-white/[0.06] p-5 flex flex-col justify-between'>
        <div className='flex items-center gap-2 text-text-primary font-extrabold text-xs uppercase tracking-widest mb-5'>
          <PieChart size={15} className='text-amber-500 shrink-0' />
          Resultados & Prep
        </div>

        <div className='flex-1 flex flex-col justify-between gap-3'>
          {resultsStats.map((stat) => (
            <div
              key={stat.name}
              className='flex items-center justify-between bg-white/[0.02] border border-white/[0.04] p-3.5 rounded-xl transition-all hover:bg-white/[0.04] hover:border-accent/10'
            >
              <div className='flex items-center gap-2.5'>
                <div className='w-1.5 h-1.5 rounded-full animate-pulse' style={{ backgroundColor: stat.color }} />
                <span className='text-[10px] font-bold text-text-muted uppercase tracking-wider'>
                  {stat.name}
                </span>
              </div>
              <div className='text-xs font-bold text-text-primary'>
                {stat.value}
                {stat.target !== null && (
                  <span className='text-text-muted font-medium ml-1'>/ {stat.target}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  )
}
